import React, { useState, useEffect, useRef } from 'react';
import Lenis from 'lenis';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import Home from './pages/Home/Home';
import CourseDetailsPage from './pages/CourseDetails/CourseDetailsPage';
import ContactPage from './pages/Contact/ContactPage';
import FounderPage from './pages/Founder/FounderPage';
import AboutPage from './pages/About/AboutPage';
import './App.css';

function App() {
  const [currentPage, setCurrentPage] = useState('home'); // 'home' | 'course-details' | 'contact' | 'founder' | 'about'
  const [selectedCourse, setSelectedCourse] = useState(null);
  const lenisRef = useRef(null);

  // 1. Initialize Lenis Smooth Scroll
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 2,
    });

    lenisRef.current = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  // 2. Guarantee scroll position resets to absolute top (0, 0) on every page view change
  useEffect(() => {
    window.scrollTo(0, 0);
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  }, [currentPage]);

  // 3. IntersectionObserver for Scroll Reveal Animations across all pages & sections
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.1,
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    const revealElements = document.querySelectorAll('.reveal-on-scroll, .reveal-up, .reveal-down, .reveal-left, .reveal-right, .reveal-zoom, .reveal-stagger');
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [currentPage]);

  const handleSelectCourse = (course) => {
    setSelectedCourse(course);
    setCurrentPage('course-details');
    window.scrollTo(0, 0);
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    }
  };

  const scrollToSectionTop = (sectionId) => {
    if (sectionId === 'home') {
      window.scrollTo(0, 0);
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, { immediate: true });
      }
      return;
    }

    const el = document.getElementById(sectionId);
    if (el) {
      const navbarOffset = 80;
      const targetY = el.getBoundingClientRect().top + window.pageYOffset - navbarOffset;

      if (lenisRef.current) {
        lenisRef.current.scrollTo(targetY, { duration: 1.2 });
      } else {
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, { immediate: true });
      }
    }
  };

  const handleNavigateToSection = (sectionId) => {
    if (sectionId === 'about') {
      setCurrentPage('about');
      window.scrollTo(0, 0);
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, { immediate: true });
      }
      return;
    }

    if (sectionId === 'contact') {
      setCurrentPage('contact');
      window.scrollTo(0, 0);
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, { immediate: true });
      }
      return;
    }

    if (sectionId === 'founder') {
      setCurrentPage('founder');
      window.scrollTo(0, 0);
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, { immediate: true });
      }
      return;
    }

    if (currentPage !== 'home') {
      setCurrentPage('home');
      setTimeout(() => {
        scrollToSectionTop(sectionId);
      }, 100);
    } else {
      scrollToSectionTop(sectionId);
    }
  };

  return (
    <div className="App">
      <Navbar 
        currentPage={currentPage} 
        onNavigateToSection={handleNavigateToSection}
        onSelectCourse={handleSelectCourse}
      />

      <main className="main-content">
        {currentPage === 'course-details' ? (
          <CourseDetailsPage 
            course={selectedCourse} 
            onSelectCourse={handleSelectCourse}
            onNavigateToContact={() => handleNavigateToSection('contact')} 
          />
        ) : currentPage === 'contact' ? (
          <ContactPage />
        ) : currentPage === 'founder' ? (
          <FounderPage 
            onSelectCourse={handleSelectCourse}
            onNavigateToContact={() => handleNavigateToSection('contact')}
          />
        ) : currentPage === 'about' ? (
          <AboutPage 
            onNavigateToContact={() => handleNavigateToSection('contact')}
          />
        ) : (
          <Home 
            onSelectCourse={handleSelectCourse} 
            onNavigateToContact={() => handleNavigateToSection('contact')} 
          />
        )}
      </main>

      <Footer onNavigateToSection={handleNavigateToSection} />
    </div>
  );
}

export default App;
